import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Label } from "recharts";
import { Box, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useThemeContext } from "../../../Context/ThemeContext";
import "./TodaySales.css";
import ArrowButton from "../../../Components/Common/ArrowButton";
import { DARKCOLORS, LIGHTCOLORS, ROUTES } from "../../../Utils/constants";
import { getTodaySales } from "../../../Services/authService";
import LoadingAnimation from "../../../Components/Common/LoadingAnimation";

const TodaySales = ({ machineType }) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();

  const [loading, setLoading] = useState(false);
  const [membershipSales, setMembershipSales] = useState(0);
  const [creditSales, setCreditSales] = useState(0);
  const [totalSales, setTotalSales] = useState(0);

  const textColor =
    mode === "dark"
      ? DARKCOLORS.HeadingLightBackground
      : LIGHTCOLORS.HeadingLightBackground;

  const subTextColor =
    mode === "dark"
      ? DARKCOLORS.BodyLightBackground
      : LIGHTCOLORS.BodyLightBackground;

  const creditArcColor =
    mode === "dark" ? DARKCOLORS.Secondary500 : LIGHTCOLORS.Secondary500;

  const shipArcColor =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;

  const GRAY_COLOR = "#B0B0B0";

  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        setLoading(true);

        const data = await getTodaySales();

        const memSales = isReturnOnly
          ? data?.paymentTypeTotals?.["NAF Wallet"] || 0
          : data?.paymentTypeTotals?.["Membership Card"] || 0;

        const credSales = isReturnOnly
          ? data?.paymentTypeTotals?.["Reverse Card"] || 0
          : data?.paymentTypeTotals?.["Credit/Debit Card"] || 0;

        setMembershipSales(memSales);
        setCreditSales(credSales);
        setTotalSales(data?.totalSales || 0);
      } catch (err) {
        console.log(err.message || t("dashboard.errorFetchingTodaySales"));
      } finally {
        setLoading(false);
      }
    };

    fetchSalesData();
  }, [t]);

  const isReturnOnly = machineType === "RETURN_ONLY";

  const title = isReturnOnly
    ? t("dashboard.todayReturns")
    : t("dashboard.todaySales");

  const totalLabel = t("dashboard.todayTotal");

  const secondLabel = isReturnOnly
    ? t("dashboard.reverseCard")
    : t("membershipCards.cardType");

  const hasData = membershipSales !== 0 || creditSales !== 0;

  /*
   * Pie chart data
   */

  const data = hasData
    ? [
        {
          name: "Membership card",
          value: membershipSales,
        },
        {
          name: "Credit/Debit Card",
          value: creditSales,
        },
      ]
    : [
        {
          name: "No Data",
          value: 1,
        },
      ];

  const COLORS = hasData ? [shipArcColor, creditArcColor] : [GRAY_COLOR];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        maxWidth: "100%",
      }}
    >
      {loading && <LoadingAnimation />}

      {/* Header */}
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ width: "100%" }}
      >
        <h3
          className="subheading-sec-card"
          style={{
            margin: "0px",
            color: textColor,
          }}
        >
          {title}
        </h3>

        <ArrowButton targetRoute={ROUTES.SALES_ANALYTICS} />
      </Grid>

      {/* Responsive Pie Chart */}
      <Box
        sx={{
          width: "100%",
          height: {
            xs: 180,
            sm: 250,
            md: 175,
            lg: 190,
            xl: 228,
          },
          maxWidth: 400,
        }}
      >
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={data}
              startAngle={180}
              endAngle={0}
              cx="50%"
              cy="80%"
              innerRadius="120%"
              outerRadius="135%"
              cornerRadius={15}
              paddingAngle={2}
              dataKey="value"
              stroke="none"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}

              <Label
                value={`€ ${totalSales}`}
                position="center"
                dy={-40}
                className="label-head-text"
                style={{
                  fontSize: "24px",
                  fill: subTextColor,
                }}
              />

              <Label
                value={totalLabel}
                position="center"
                dy={-10}
                dx={-7}
                className="label-text"
                style={{
                  fill: subTextColor,
                }}
              />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </Box>

      {/* Sales / Returns Breakdown */}
      <Grid container justifyContent="space-around" sx={{ width: "100%" }}>
        {/* Membership / NAF Wallet */}
        <Box textAlign="center">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: 1.5,
            }}
          >
            {hasData && (
              <Box
                sx={{
                  backgroundColor: COLORS[0],
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  display: "inline-block",
                  marginRight: 1,
                }}
              />
            )}

            <h3 className="sections-headings">€ {membershipSales}</h3>
          </Box>

          <p style={{ color: subTextColor }} className="label-text">
            {t("dashboard.nafWallet")}
          </p>
        </Box>

        {/* Credit / Debit Card */}
        <Box textAlign="center">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: 1.5,
            }}
          >
            {hasData && (
              <Box
                sx={{
                  backgroundColor: COLORS[1],
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  display: "inline-block",
                  marginRight: 1,
                }}
              />
            )}

            <h3 className="sections-headings">€ {creditSales}</h3>
          </Box>

          <p style={{ color: subTextColor }} className="label-text">
            {secondLabel}
          </p>
        </Box>
      </Grid>
    </Box>
  );
};

export default TodaySales;
